import React from 'react';
import { Language } from '../types/kundali';
import { AdvancedLifeAnalysisResult } from '../types/advancedLifeAnalysis';
import { ShieldCheck, Heart, AlertTriangle, Sparkles, Compass, CheckCircle2, Info } from 'lucide-react';

interface RemediesGuidanceCardProps {
  analysisResult: AdvancedLifeAnalysisResult;
  lang: Language;
  className?: string;
}

export const RemediesGuidanceCard: React.FC<RemediesGuidanceCardProps> = ({
  analysisResult,
  lang,
  className = ''
}) => {
  const isHi = lang === 'hi';
  const { remedies, lifeStageContext, overallFeedbackEn, overallFeedbackHi } = analysisResult;

  const mantras = remedies.filter((r) => r.type === 'MANTRA');
  const charities = remedies.filter((r) => r.type === 'CHARITY');
  const gemstones = remedies.filter((r) => r.type === 'GEMSTONE');
  const lifestyles = remedies.filter((r) => r.type === 'LIFESTYLE');

  return (
    <div
      id="remedies-guidance-card"
      className={`bg-astro-card border border-astro-border rounded-2xl p-5 sm:p-6 shadow-2xs space-y-5 ${className}`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-astro-border pb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-astro-accent" />
          <h3 className="text-sm sm:text-base font-bold uppercase tracking-wider text-astro-text">
            {isHi ? 'शास्त्रीय वैदिक उपाय एवं व्यावहारिक जीवन-मार्गदर्शन' : 'Vedic Remedies & Life-Stage Guidance'}
          </h3>
        </div>
        <span className="text-xs text-astro-primary font-semibold bg-astro-teal-tint px-2.5 py-0.5 rounded-full border border-astro-primary/20 self-start sm:self-auto">
          {isHi ? lifeStageContext.stage.labelHi : lifeStageContext.stage.labelEn}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Left: Prescriptive Vedic Remedies */}
        <div className="space-y-3.5">
          <h4 className="text-xs font-bold uppercase tracking-wider text-astro-text flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-astro-accent" />
            <span>{isHi ? 'शास्त्रीय वैदिक उपाय एवं सकारात्मक कार्य' : 'Classical Remedies & Positive Actions'}</span>
          </h4>

          <div className="space-y-2.5 text-xs">
            {/* Mantras */}
            {mantras.map((m, idx) => (
              <div
                key={idx}
                className="p-3 rounded-xl bg-astro-bg border border-astro-border space-y-1 shadow-2xs"
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-astro-primary">
                    {isHi ? m.titleHi : m.titleEn}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-astro-teal-tint text-astro-primary font-semibold">
                    {isHi ? 'मंत्र' : 'Mantra'}
                  </span>
                </div>
                <p className="text-astro-secondary-text font-medium leading-relaxed">
                  {isHi ? m.instructionHi : m.instructionEn}
                </p>
              </div>
            ))}

            {/* Charities / Dana */}
            {charities.map((c, idx) => (
              <div
                key={idx}
                className="p-3 rounded-xl bg-astro-bg border border-astro-border space-y-1 shadow-2xs"
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-astro-text">
                    {isHi ? c.titleHi : c.titleEn}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-astro-bg text-astro-secondary-text font-semibold border border-astro-border">
                    {isHi ? 'दान' : 'Dana'}
                  </span>
                </div>
                <p className="text-astro-secondary-text leading-relaxed">
                  {isHi ? c.instructionHi : c.instructionEn}
                </p>
              </div>
            ))}

            {/* Gemstone / Lifestyle */}
            {gemstones.concat(lifestyles).map((g, idx) => (
              <div
                key={idx}
                className="p-3 rounded-xl bg-astro-bg border border-astro-border space-y-1 shadow-2xs"
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-astro-text">
                    {isHi ? g.titleHi : g.titleEn}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-astro-bg text-astro-secondary-text font-semibold border border-astro-border">
                    {g.type === 'GEMSTONE' ? (isHi ? 'रत्न' : 'Gemstone') : (isHi ? 'दिनचर्या' : 'Lifestyle')}
                  </span>
                </div>
                <p className="text-astro-secondary-text leading-relaxed">
                  {isHi ? g.instructionHi : g.instructionEn}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Practical Life-Stage Actionable Guidance */}
        <div className="space-y-3.5">
          <h4 className="text-xs font-bold uppercase tracking-wider text-astro-text flex items-center gap-1.5">
            <Compass className="w-4 h-4 text-astro-primary" />
            <span>{isHi ? 'वर्तमान जीवन चरण के लिए व्यावहारिक मार्गदर्शन' : 'Practical Guidance for Your Current Phase'}</span>
          </h4>

          <div className="space-y-2.5 text-xs">
            {/* Developmental Focus */}
            <div className="p-3.5 rounded-xl bg-astro-teal-tint/70 border border-astro-primary/25 space-y-1.5 shadow-2xs">
              <span className="font-bold text-astro-primary flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-astro-primary" />
                <span>{isHi ? 'वर्तमान मुख्य लक्ष्य व दिशा:' : 'Current Focus & Direction:'}</span>
              </span>
              <p className="text-astro-text leading-relaxed">
                {isHi ? lifeStageContext.stage.developmentalFocusHi : lifeStageContext.stage.developmentalFocusEn}
              </p>
            </div>

            {/* Strategic Practical Context Guidance */}
            <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1 shadow-2xs">
              <span className="font-bold text-astro-text block">
                {isHi ? 'व्यावहारिक संदर्भ:' : 'Practical Context:'}
              </span>
              <p className="text-astro-secondary-text leading-relaxed">
                {isHi ? lifeStageContext.deshKaalPatraGuidanceHi : lifeStageContext.deshKaalPatraGuidanceEn}
              </p>
            </div>

            {/* Overall Synthesized Feedback */}
            {(overallFeedbackEn || overallFeedbackHi) && (
              <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1 shadow-2xs">
                <span className="font-bold text-astro-primary flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5 text-astro-primary" />
                  <span>{isHi ? 'परामर्श सारांश:' : 'Consultation Note:'}</span>
                </span>
                <p className="text-astro-secondary-text leading-relaxed">
                  {isHi ? overallFeedbackHi : overallFeedbackEn}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
